import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, GraduationCap, BookOpen, ArrowRight, User, AlertTriangle } from 'lucide-react';
import { cn } from '../lib/utils';
import { InlineLoader } from './ui/PremiumLoader';

export interface GoogleUserData {
  firebaseUid: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string | null;
  provider?: string;
  // For existing users who might want to switch roles
  existingUser?: {
    id: string;
    currentRole: 'STUDENT' | 'PROFESSOR' | 'ADMIN';
  };
}

interface GoogleRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (role: 'STUDENT' | 'PROFESSOR') => Promise<void>;
  userData: GoogleUserData | null;
  isLoading?: boolean;
}

export function GoogleRoleModal({
  isOpen,
  onClose,
  onComplete,
  userData,
  isLoading = false,
}: GoogleRoleModalProps) {
  const [selectedRole, setSelectedRole] = useState<'STUDENT' | 'PROFESSOR'>('STUDENT');

  const handleComplete = async () => {
    await onComplete(selectedRole);
  };

  if (!isOpen || !userData) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-md mx-4 bg-void border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              {userData.avatarUrl ? (
                <img
                  src={userData.avatarUrl}
                  alt={userData.firstName}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-aurora/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-aurora-light" />
                </div>
              )}
              <div>
                <h2 className="text-lg font-semibold text-white">
                  Welcome, {userData.firstName}!
                </h2>
                <p className="text-sm text-white/50">{userData.email}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-white font-medium mb-2">
                {userData.existingUser 
                  ? 'Upgrade to Professor' 
                  : 'Complete Your Registration'}
              </h3>
              <p className="text-white/50 text-sm">
                {userData.existingUser 
                  ? `You're currently registered as a ${userData.existingUser.currentRole.toLowerCase()}. You can upgrade to professor to create and sell courses.`
                  : 'Please select how you want to use the platform. This helps us personalize your experience.'}
              </p>
            </div>

            {/* Role Selection */}
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSelectedRole('STUDENT')}
                disabled={isLoading || userData.existingUser?.currentRole === 'PROFESSOR'}
                className={cn(
                  'relative p-4 rounded-xl border transition-all duration-200 text-left group',
                  selectedRole === 'STUDENT'
                    ? 'border-aurora/50 bg-aurora/10'
                    : 'border-white/10 hover:border-white/20 bg-white/[0.02]',
                  (isLoading || userData.existingUser?.currentRole === 'PROFESSOR') && 'opacity-50 cursor-not-allowed'
                )}
              >
                <div className={cn(
                  'w-10 h-10 rounded-lg flex items-center justify-center mb-2.5 transition-colors',
                  selectedRole === 'STUDENT' ? 'bg-aurora/20' : 'bg-white/5'
                )}>
                  <GraduationCap className={cn(
                    'w-5 h-5',
                    selectedRole === 'STUDENT' ? 'text-aurora-light' : 'text-white/40'
                  )} />
                </div>
                <div className={cn(
                  'font-semibold text-sm',
                  selectedRole === 'STUDENT' ? 'text-white' : 'text-white/70'
                )}>
                  Learn
                </div>
                <div className="text-xs text-white/40 mt-0.5">Access courses and grow</div>
                {userData.existingUser?.currentRole === 'PROFESSOR' && (
                  <div className="text-xs text-amber-400/70 mt-1">Not available for professors</div>
                )}
              </button>

              <button
                type="button"
                onClick={() => setSelectedRole('PROFESSOR')}
                disabled={isLoading}
                className={cn(
                  'relative p-4 rounded-xl border transition-all duration-200 text-left group disabled:opacity-50',
                  selectedRole === 'PROFESSOR'
                    ? 'border-aurora/50 bg-aurora/10'
                    : 'border-white/10 hover:border-white/20 bg-white/[0.02]'
                )}
              >
                <div className={cn(
                  'w-10 h-10 rounded-lg flex items-center justify-center mb-2.5 transition-colors',
                  selectedRole === 'PROFESSOR' ? 'bg-aurora/20' : 'bg-white/5'
                )}>
                  <BookOpen className={cn(
                    'w-5 h-5',
                    selectedRole === 'PROFESSOR' ? 'text-aurora-light' : 'text-white/40'
                  )} />
                </div>
                <div className={cn(
                  'font-semibold text-sm',
                  selectedRole === 'PROFESSOR' ? 'text-white' : 'text-white/70'
                )}>
                  Teach
                </div>
                <div className="text-xs text-white/40 mt-0.5">Create and sell courses</div>
              </button>
            </div>

            {/* Warning for Professor Selection */}
            {selectedRole === 'PROFESSOR' && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-amber-200 font-medium">This is a permanent change</p>
                  <p className="text-amber-200/70 mt-1">
                    Once you become a professor, you cannot switch back to a student account. 
                    This protects your courses and students who enroll in them.
                  </p>
                </div>
              </div>
            )}

            {/* Terms Notice */}
            <p className="text-xs text-white/30 text-center">
              By continuing, you agree to our{' '}
              <a href="/terms" className="text-aurora-light hover:text-aurora">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" className="text-aurora-light hover:text-aurora">Privacy Policy</a>
            </p>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-white/10 bg-white/[0.02]">
            <button
              onClick={handleComplete}
              disabled={isLoading}
              className="btn-primary w-full group"
            >
              {isLoading ? (
                <>
                  <InlineLoader className="mr-2" />
                  <span>{userData?.existingUser ? 'Upgrading account...' : 'Creating account...'}</span>
                </>
              ) : (
                <>
                  <span>
                    {selectedRole === 'PROFESSOR'
                      ? (userData?.existingUser 
                          ? 'Upgrade to Professor (Permanent)' 
                          : 'Continue as Professor (Permanent)')
                      : 'Continue as Student'}
                  </span>
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
